import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { docT } from "./doc-locale";
import { DocTerm } from "./DocInteractive";
import "./doc-email.css";

// The e-mail blueprint leaves (LAYOUT-BLUEPRINT-EMAILS.md, R1-R3): the card an
// `<email>` renders as, the vertical campaign map `<timeline>`/`<phase>`/`<tl>`
// draws, and the inline `<ref to>` jump. They live next to DocInteractive.tsx
// under the same three rules: nothing is injected (these are components the
// whitelist in DocHtml.tsx decides to place), every affordance answers hover,
// keyboard AND tap, and a control that cannot do its job is not drawn as a
// control - a map row or a ref whose e-mail card does not exist is plain text.

function reduceMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/**
 * Open the card (or the fold) an id names and bring it under the reader's
 * eyes. R3's whole contract: the pre-flight checklist points back INTO the
 * document, and a jump that lands on a closed card would look like a jump to
 * nothing.
 */
export function jumpToDocId(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (el instanceof HTMLDetailsElement) el.open = true;
  else el.closest("details")?.setAttribute("open", "");
  el.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
}

/** Inline `<ref to="email-01">` - a jump only when the target exists. */
export function DocRef(props: { target: string | null; children: ReactNode }) {
  const target = props.target;
  if (!target) return <span className="doc-ref is-static">{props.children}</span>;
  return (
    <button type="button" className="doc-ref" onClick={() => jumpToDocId(target)}>
      {props.children}
    </button>
  );
}

/* ------------------------------------------------------------ e-mail card -- */

/**
 * The card an `<email>` tag renders as (blueprint 4.1). The header IS the fold
 * toggle: numeral, name, day chip, segment chip, auto chip, and the task as a
 * muted second line. Nothing about an e-mail renders outside its card.
 */
export function DocEmailCard(props: {
  id?: string;
  n: string;
  name: string;
  day?: string;
  time?: string;
  /** Human phrase for a triggered send, already resolved by the renderer. */
  autoLabel?: string;
  task?: string;
  segClass?: string;
  segLabel?: string;
  segTip?: string;
  children: ReactNode;
}) {
  // A chip tap inside <summary> must toggle the TOOLTIP, not the card: the
  // default details toggle is cancelable, the tooltip click is not.
  const onChipsClick = (event: MouseEvent) => {
    if (event.target instanceof Element && event.target.closest(".doc-term-wrap")) event.preventDefault();
  };
  const day = [props.day, props.time].filter(Boolean).join(", ");
  return (
    <details id={props.id} className={["doc-email", props.segClass].filter(Boolean).join(" ")}>
      <summary className="doc-email-head">
        <span className="doc-email-num" aria-hidden="true">{props.n}</span>
        <span className="doc-email-title">
          <span className="doc-email-name">{props.name}</span>
          <span className="doc-email-chips" onClick={onChipsClick}>
            {day && <span className="doc-email-day">{day}</span>}
            {props.autoLabel && <span className="doc-email-auto">{props.autoLabel}</span>}
            {props.segLabel && (
              <DocTerm tip={props.segTip ?? ""} segClass={[props.segClass, "doc-seg-chip"].filter(Boolean).join(" ")}>
                {props.segLabel}
              </DocTerm>
            )}
          </span>
          {props.task && <span className="doc-email-task">{props.task}</span>}
        </span>
        <span className="doc-email-mark" aria-hidden="true" />
      </summary>
      <div className="doc-email-fields">{props.children}</div>
    </details>
  );
}

/* ----------------------------------------------------------- campaign map -- */

export interface DocMapRow {
  n: string;
  day: string;
  name: string;
  /** Chip text for a triggered send, or null for an ordinary one. */
  auto: string | null;
  /** E-mail card id this row opens, or null when the card does not exist. */
  target: string | null;
  segClass?: string;
  segLabel?: string;
  segTip?: string;
}

export interface DocMapPhase {
  label: string;
  note?: string;
  rows: DocMapRow[];
}

/**
 * The vertical campaign map (blueprint 4.2): phases as amber group headers,
 * one row per e-mail. A click opens the `<email n>` card and scrolls to it;
 * the reading position feeds the active-row mark the same way the horizontal
 * axis already does, so the map answers "where am I" without being clicked.
 */
export function DocCampaignMap(props: { caption?: string; phases: DocMapPhase[] }) {
  const rows = useMemo(() => props.phases.flatMap((phase) => phase.rows), [props.phases]);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const targets = rows
      .map((row, index) => ({ index, el: row.target ? document.getElementById(row.target) : null }))
      .filter((entry): entry is { index: number; el: HTMLElement } => entry.el !== null);
    if (targets.length === 0) return undefined;
    const visible = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.set(entry.target, entry.boundingClientRect.top);
        else visible.delete(entry.target);
      }
      if (visible.size === 0) return;
      const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
      const match = targets.find((entry) => entry.el === top);
      if (match) setActive(match.index);
    }, { rootMargin: "-10% 0px -70% 0px", threshold: 0 });
    targets.forEach((entry) => observer.observe(entry.el));
    return () => observer.disconnect();
  }, [rows]);

  const indexOf = useMemo(() => new Map(rows.map((row, index) => [row, index])), [rows]);

  const renderRow = (row: DocMapRow, rowKey: string) => {
    const index = indexOf.get(row) ?? 0;
    const body = (
      <>
        <span className="doc-map-day">{row.day}</span>
        <span className="doc-map-dot" aria-hidden="true" />
        <span className="doc-map-name">
          {row.n && <span className="doc-map-num">{row.n}</span>}
          {row.name}
        </span>
      </>
    );
    return (
      <div
        key={rowKey}
        className={["doc-map-row", row.segClass, active === index ? "is-active" : ""].filter(Boolean).join(" ")}
      >
        {row.target ? (
          <button
            type="button"
            className="doc-map-jump"
            aria-current={active === index ? "step" : undefined}
            aria-label={`${docT("doc.timeline.jump")}: ${[row.day, row.n, row.name].filter(Boolean).join(", ")}`}
            onClick={() => { setActive(index); jumpToDocId(row.target as string); }}
          >
            {body}
          </button>
        ) : (
          // No card to open: a button here would be dead UI.
          <span className="doc-map-jump is-static" aria-current={active === index ? "step" : undefined}>
            {body}
          </span>
        )}
        <span className="doc-map-meta">
          {row.auto && <span className="doc-map-auto">{row.auto}</span>}
          {row.segLabel && (
            <DocTerm tip={row.segTip ?? ""} segClass={[row.segClass, "doc-seg-chip"].filter(Boolean).join(" ")}>
              {row.segLabel}
            </DocTerm>
          )}
        </span>
      </div>
    );
  };

  return (
    <nav className="doc-map" aria-label={docT("doc.map.aria")}>
      {props.caption && <p className="doc-map-caption">{props.caption}</p>}
      {props.phases.map((phase, phaseIndex) => (
        <section className="doc-map-phase" key={`${phase.label}-${phaseIndex}`}>
          {(phase.label || phase.note) && (
            <header className="doc-map-phase-head">
              {phase.label && <span className="doc-map-phase-label">{phase.label}</span>}
              {phase.note && <span className="doc-map-phase-note">{phase.note}</span>}
            </header>
          )}
          <div className="doc-map-rows">
            {phase.rows.map((row, rowIndex) => renderRow(row, `${phaseIndex}-${rowIndex}`))}
          </div>
        </section>
      ))}
    </nav>
  );
}
